import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import SEO from "@/components/SEO";

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category));
    return ["All", ...Array.from(set).sort()];
  }, [products]);

  const filtered = category === "All" ? products : products.filter((p) => p.category === category);

  return (
    <div data-testid="catalog-page" className="max-w-7xl mx-auto px-6 md:px-10 py-16">
      <SEO
        title="Dessert Collection — Wholesale cakes"
        description="Signature wholesale desserts available in 3″ Mini and 10″ Whole formats — Chajá, NY Cheesecake, Chocolate, Tiramisu and Karpatka. Request personalized pricing."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: products.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://cakeworldusa.com/product/${p.slug}`,
            name: p.name,
          })),
        }}
      />
      <div className="max-w-2xl">
        <div className="eyebrow">Dessert Collection</div>
        <h1 className="font-display text-5xl sm:text-6xl text-brand-ink mt-2 leading-none">Our signature desserts.</h1>
        <p className="mt-6 text-brand-stone leading-relaxed font-ui">
          Every dessert below is available in two wholesale formats — a 3″ Mini Cake and a 10″ Whole Cake. Add products to your quote cart to receive personalized wholesale pricing.
        </p>
      </div>

      <div className="dot-divider mt-10" />

      <div className="mt-10 flex flex-wrap gap-2" data-testid="catalog-filters">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`filter-${c.toLowerCase()}`}
            className={`px-4 h-10 rounded-full text-sm font-semibold transition border ${
              category === c
                ? "bg-brand-ink text-white border-brand-ink"
                : "bg-transparent text-brand-ink border-brand-line hover:border-brand-ink"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalog-grid">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-[4/5] rounded-2xl bg-brand-pink/30 animate-pulse" />
          ))
        ) : filtered.length === 0 ? (
          <div className="col-span-full text-center py-16 text-brand-stone">No products in this category yet.</div>
        ) : (
          filtered.map((p) => <ProductCard key={p.id} product={p} />)
        )}
      </div>
    </div>
  );
}
